import type { EvalContext } from '../EvalContext.ts';
import { evalExpr } from '../evaluator.ts';
import type { Value } from '../value.ts';
import { useBindingResolver } from './BindingScopeContext.ts';
import { type BindingResolver, createBindingResolver, type Recipe } from './bindingResolver.ts';
import { type EvalDeps, useEvalDeps } from './EvalDepsContext.ts';
import type { Session } from './Session.ts';
import { useSession } from './SessionContext.ts';

/**
 * Build an {@link EvalContext} from a mounted `Session`, the optional
 * fields on {@link EvalDeps}, and a {@link BindingResolver}. Shared by
 * every React-side evaluator hook (`useEvalExpr`, `useMaterialize`,
 * `useMaterializedExpr`) and by `createSessionBindingResolver` — the
 * single source of truth for "spread deps + bindings onto a base
 * EvalContext."
 */
export function buildEvalContext(
  session: EvalContext['session'],
  deps: EvalDeps,
  bindings: BindingResolver,
): EvalContext {
  return {
    session,
    bindings,
    ...deps,
  };
}

/**
 * Build a {@link BindingResolver} that re-derives cross-turn dataset
 * values by evaluating each producer recipe's `expression` against the
 * strict prefix before it. This is the single place that closes the
 * recursion: a nested `(col <name> …)` inside a producer expression
 * resolves through the same `session`/`deps`, scoped to a child
 * resolver, memoized via `session.fetch`.
 *
 * `recipes` is a position-scoped snapshot derived from the active
 * message branch (the chat layer's `useBindingScope()` supplies it);
 * non-chat callers pass `[]` (equivalent to `emptyBindingResolver`).
 */
export const createSessionBindingResolver = (
  recipes: readonly Recipe[],
  session: Session,
  deps: EvalDeps,
): BindingResolver =>
  createBindingResolver(recipes, session, (expression, bindings) =>
    evalExpr(expression, buildEvalContext(session, deps, bindings), deps.ops),
  );

/**
 * React hook returning an async `(expr) => Promise<Value>` bound to
 * the `Session` from `SessionContext`, the {@link EvalDeps} from
 * `EvalDepsContext`, and the {@link BindingResolver} in scope from
 * `BindingScopeContext` (defaults applied when no provider is mounted).
 * This is the canonical entry point for evaluating s-expressions inside
 * React — assembles an `EvalContext` from React state and threads it
 * into `evalExpr`.
 *
 * Free names in `expr` resolve against the ambient binding scope: an
 * empty scope (no provider, or a render position before any producer)
 * surfaces a normal `UnboundSymbolError`; a tool render that mounts a
 * position-scoped resolver (`useBindingScope()`) resolves cross-turn
 * `(col <name> …)` references against exactly the producers visible at
 * that point.
 *
 * The returned function is stable per session reference — React
 * Compiler memoizes the closure, so this hook can be called in render
 * without churning effect dependency arrays.
 *
 * @throws via `useSession()` if no `<SessionContext>` provider is
 *   mounted upstream (intentional hard failure — see `SessionContext`
 *   rationale). Missing `EvalDepsContext` / `BindingScopeContext` is
 *   *not* an error; both fall back to engine defaults.
 *
 * @example
 * ```tsx
 * function MyComponent() {
 *   const evaluate = useEvalExpr();
 *   const onClick = async () => {
 *     const result = await evaluate('(+ 1 2)');
 *     console.log(result); // 3
 *   };
 *   return <button onClick={onClick}>Run</button>;
 * }
 * ```
 */
export function useEvalExpr(): (expr: string) => Promise<Value> {
  const session = useSession();
  const deps = useEvalDeps();
  const bindings = useBindingResolver();
  return (expr: string) => evalExpr(expr, buildEvalContext(session, deps, bindings), deps.ops);
}
